import config, { isHome } from '~/config'

import isResponseOk from '~/utils/isResponseOk'
import toJSON from '~/utils/toJSON'

const sharedConfig: any = {
  credentials: 'include',
  headers: { 'Content-Type': 'application/json' },
  method: 'POST',
}

const AUTH_TOKEN_KEY = 'hodorAuthToken'

// In-memory copy of the token, and the source of truth for this tab. localStorage
// can refuse a write — a full origin quota, or storage blocked outright — and losing
// that cache must not cost us a session that Hodor considers valid. Reading memory
// first also stops a failed write from leaving an older token in play. See PES-11605.
let cachedAuthToken: Nullable<string> = null

export const clearAuthToken = () => {
  cachedAuthToken = null

  try {
    localStorage.removeItem(AUTH_TOKEN_KEY)
  } catch {
    // Storage is unreachable; dropping the in-memory copy is all we can do.
  }
}

export const getAuthToken = () => {
  if (cachedAuthToken) return cachedAuthToken

  try {
    return localStorage.getItem(AUTH_TOKEN_KEY)
  } catch {
    return null
  }
}

/**
 * Caches the token for subsequent requests. Returns false when it could not be
 * written to localStorage — the token is still held in memory and usable for this
 * tab, so callers should report the failure rather than end the session.
 */
export const setAuthToken = (token: string) => {
  cachedAuthToken = token

  try {
    localStorage.setItem(AUTH_TOKEN_KEY, token)
    return true
  } catch {
    return false
  }
}

export const fetchWithAuth = (uri: RequestInfo, options: RequestInit) => {
  const token = getAuthToken()
  return fetch(uri, {
    ...options,
    headers: new Headers({
      // Allow the auth header to be overridden if needed
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    }),
  })
}

export const logout = () => {
  clearAuthToken()

  return fetch(`${config.HODOR_API}/logout`, {
    credentials: 'include',
    method: 'POST',
  })
}

export const redirectToLogin = () => {
  isHome
    ? window.location.assign(`${config.STARGATE_URL}/?app=home-web`)
    : window.location.assign(`${config.STARGATE_URL}/?app=playground`)
}

export default (uri: string) => {
  return {
    refresh() {
      return fetch(`${uri}/refresh`, sharedConfig)
        .then(isResponseOk)
        .then(toJSON)
        .then((data) => data.data.token)
    },
  }
}
